import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addNewUser } from "../../../slices/thunks";
import { createSelector } from "reselect";

const AddUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectLayoutState = (state) => state.User;
  const selectLayoutProperties = createSelector(selectLayoutState, (user) => ({
    users: user.users,
    isUserSuccess: user.isUserSuccess,
    error: user.error,
  }));

  const { users } = useSelector(selectLayoutProperties);
  const validation = useFormik({
    initialValues: {
      username: "",
      password1: "",
      password2: "",
      group: "",
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .matches(
          /^[a-zA-Z0-9@.+-_]+$/,
          "Enter a valid username. This value may contain only letters, numbers, and @/./+/-/_ characters."
        )
        .required("Please Enter Your Username")
        .test("unique-username", "Username already exists", function (value) {
          const usernameExists = users.some((user) => user.username === value);
          return !usernameExists;
        }),
      password1: Yup.string()
        .required("Please enter your Password")
        .min(8, "Password must be at least 8 characters long")
        .matches(
          /^(?=.*[0-9])(?=.*[A-Za-z]).*$/,
          "Password must contain both letters and numbers"
        )
        .test(
          "attribute-similarity",
          "Password is too similar to your username or email",
          function (value) {
            const { username, email } = this.parent;
            return (
              value &&
              !(
                (username && value.includes(username)) ||
                (email && value.includes(email.split("@")[0]))
              )
            );
          }
        )
        .test(
          "numeric-password",
          "Password must not be entirely numeric",
          (value) => value && !/^\d+$/.test(value)
        ),
      password2: Yup.string()
        .oneOf([Yup.ref("password1")], "Passwords do not match")
        .required("Please confirm your password"),
      group: Yup.string().required("Please select a group"),
    }),
    onSubmit: (values) => {
      dispatch(addNewUser(values))
        .then((response) => {
          if (response?.payload) {
            navigate(-1);
          } else {
            console.error("Failed to create user");
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
  });

  return (
    <div className="container my-4">
      <h1 className="mb-4">Add New User</h1>
      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={validation.handleSubmit}>
            <div className="mb-3">
              <label htmlFor="username" className="form-label">
                Username
              </label>
              <input
                type="text"
                id="username"
                className="form-control"
                placeholder="Enter username"
                value={validation.values.username}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                required
              />
              {validation.touched.username && validation.errors.username && (
                <div className="text-danger">{validation.errors.username}</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="password1" className="form-label">
                Password
              </label>
              <input
                type="password"
                id="password1"
                className="form-control"
                placeholder="Enter password"
                value={validation.values.password1}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                required
              />
              {validation.touched.password1 && validation.errors.password1 && (
                <div className="text-danger">{validation.errors.password1}</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="password2" className="form-label">
                Confirm Password
              </label>
              <input
                type="password"
                id="password2"
                className="form-control"
                placeholder="Confirm password"
                value={validation.values.password2}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                required
              />
              {validation.touched.password2 && validation.errors.password2 && (
                <div className="text-danger">{validation.errors.password2}</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="group" className="form-label">
                Select Group
              </label>
              <select
                id="group"
                className="form-select"
                value={validation.values.group}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                required
              >
                <option value="">Choose group...</option>
                <option value="1">Viewer</option>
                <option value="2">Editor</option>
                <option value="3">Admin</option>
              </select>
              {validation.touched.group && validation.errors.group && (
                <div className="text-danger">{validation.errors.group}</div>
              )}
            </div>

            {validation.errors.submit && (
              <div className="alert alert-danger">
                {validation.errors.submit}
              </div>
            )}

            <div className="d-flex justify-content-between">
              <button type="submit" className="btn btn-primary">
                Add User
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
